import React, { useCallback, useLayoutEffect, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { useTimeout } from './timeout.hook';

export interface ElementSizeHookProps {
  once?: boolean;
  elementRef: React.MutableRefObject<HTMLElement | null>;
}

// get width and height of element
export function useElementSize(
  props: ElementSizeHookProps
): [number | undefined, number | undefined] {
  const { once = false, elementRef } = props;
  const [width, setWidth] = useState<number | undefined>();
  const [height, setHeight] = useState<number | undefined>();
  const [observer, setObserver] = useState<ResizeObserver | undefined>();
  const [debounceUpdate, clearDebouncedUpdate] = useTimeout();

  // get size of container
  const getElementSize = useCallback((): [
    number | undefined,
    number | undefined
  ] => {
    if (!elementRef.current) {
      return [undefined, undefined];
    }
    const rect: DOMRect = elementRef.current.getBoundingClientRect();
    return [rect.width, rect.height];
  }, [elementRef]);

  const updateElementSize = useCallback(() => {
    const [width, height] = getElementSize();
    setWidth(width);
    setHeight(height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getElementSize]);

  useLayoutEffect(() => {
    observer?.disconnect();
    const ob: ResizeObserver = new ResizeObserver(() => {
      debounceUpdate(() => {
        if (!once || !(width && height)) {
          updateElementSize();
        }
      }, 500);
    });
    setObserver(ob);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [once, width, height]);

  useLayoutEffect(() => {
    const el: HTMLElement | null = elementRef.current;
    if (el && observer) {
      observer.observe(el);
      updateElementSize();
    }
    return () => {
      clearDebouncedUpdate();
      if (el) {
        observer?.unobserve(el);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, observer]);

  return [width, height];
}
